import { useQuery } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportTicketEvent } from '../../models/Support/ISupportTicketEvent';
import {
	SUPPORT_PATHS,
	SUPPORT_POLL_INTERVAL_MS,
	SUPPORT_QUERY_KEYS,
	supportPollingOptions,
} from './supportQueryHelpers';

interface SupportTicketEventsQueryOptions {
	/** Override the default polling interval (in ms). Pass `false` to disable polling. */
	refetchInterval?: number | false;
}

/**
 * Fetches the structured activity events for a ticket
 * (`GET support/tickets/{id}/events` → `{ values: ISupportTicketEvent[] }`).
 *
 * Polls on the same cadence as {@link useSupportThreadsQuery} so the merged
 * conversation timeline stays in sync. Requires the caller be an agent for the
 * ticket's mailbox (else the API returns 403).
 */
export function useSupportTicketEventsQuery(ticketId: string, options: SupportTicketEventsQueryOptions = {}) {
	const { refetchInterval = SUPPORT_POLL_INTERVAL_MS } = options;
	const userSession = useUserSession();

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.events(ticketId),
		...supportPollingOptions(refetchInterval),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IPagedCollection<ISupportTicketEvent>>(
				SUPPORT_PATHS.tickets.events(ticketId),
				'GET'
			),
		enabled: !!ticketId,
		// No toast on failure: a broken events endpoint re-errors on every poll cycle, which
		// would re-toast every 10s. ConversationPane surfaces the failure inline instead.
	});
}
